import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from '../utils/toast';
import { staffAPI, confidentialAPI } from '../services/api';

const invalidate = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['workPass'] });
  queryClient.invalidateQueries({ queryKey: ['terminationRequests'] });
};

export const useWorkPassCapabilities = (options = {}) =>
  useQuery({
    queryKey: ['workPass', 'capabilities'],
    queryFn: async () => {
      const response = await staffAPI.getWorkPassCapabilities();
      return response.data || {};
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const useWorkPassQueue = (tab = 'my_actions', options = {}) =>
  useQuery({
    queryKey: ['workPass', 'queue', tab],
    queryFn: async () => {
      const response = await staffAPI.getWorkPassQueue(tab);
      return response.data || [];
    },
    staleTime: 30 * 1000,
    ...options,
  });

export const useWorkPassDecision = (id, options = {}) =>
  useQuery({
    queryKey: ['workPass', 'detail', id],
    queryFn: async () => {
      const response = await staffAPI.getWorkPassDecision(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 15 * 1000,
    ...options,
  });

export const useRecommendWorkPass = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.recommendWorkPassDecision(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Recommendation recorded');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not record recommendation');
    },
  });
};

export const useApproveWorkPass = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.approveWorkPassDecision(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Decision approved');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not approve this decision');
    },
  });
};

export const useReturnWorkPass = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.returnWorkPassDecision(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Returned for a new recommendation');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not return this decision');
    },
  });
};

export const useHRWorkPassDecisions = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['workPass', 'hr', 'decisions', params],
    queryFn: async () => {
      const response = await confidentialAPI.getHRWorkPassDecisions(params);
      return response.data || [];
    },
    staleTime: 30 * 1000,
    ...options,
  });

export const useHRWorkPassRenewals = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['workPass', 'hr', 'renewals', params],
    queryFn: async () => {
      const response = await confidentialAPI.getHRWorkPassRenewals(params);
      return response.data || [];
    },
    staleTime: 30 * 1000,
    ...options,
  });

export const useHRWorkPassDecision = (id, options = {}) =>
  useQuery({
    queryKey: ['workPass', 'hr', 'detail', id],
    queryFn: async () => {
      const response = await confidentialAPI.getHRWorkPassDecision(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 15 * 1000,
    ...options,
  });

export const useResolveWorkPassException = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.resolveWorkPassException(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Configuration exception resolved');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not resolve this exception');
    },
  });
};

export const useUpdateWorkPassRenewal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => confidentialAPI.updateHRWorkPassRenewal(id, data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Renewal updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Could not update renewal');
    },
  });
};
